import { useEffect, useMemo } from 'react';

declare global {
  interface Window {
    JustWatch?: {
      reloadWidgets: () => void;
    };
  }
}

interface JustWatchWidgetProps {
  mediaType: 'movie' | 'show';
  tmdbId: number;
  imdbId?: string | null;
  title: string;
  year?: number;
  className?: string;
}

export function JustWatchWidget({
  mediaType,
  tmdbId,
  imdbId,
  title,
  year,
  className = '',
}: JustWatchWidgetProps) {
  const apiKey = import.meta.env.VITE_JUSTWATCH_WIDGET_TOKEN?.trim();
  const brandingLinkId = useMemo(() => `jw-branding-${mediaType}-${tmdbId}`, [mediaType, tmdbId]);

  const matchConfig = useMemo(() => {
    const normalizedImdbId = imdbId?.trim();
    if (normalizedImdbId && /^tt\d+$/i.test(normalizedImdbId)) {
      return {
        id: normalizedImdbId,
        idType: 'imdb' as const,
      };
    }

    if (Number.isFinite(tmdbId) && tmdbId > 0) {
      return {
        id: String(tmdbId),
        idType: 'tmdb' as const,
      };
    }

    return {
      title: title.trim(),
      year: year ? String(year) : undefined,
    };
  }, [imdbId, tmdbId, title, year]);

  const reloadKey = useMemo(() => {
    if ('id' in matchConfig) {
      return `${mediaType}:${matchConfig.idType}:${matchConfig.id}`;
    }
    return `${mediaType}:title:${matchConfig.title}:${matchConfig.year || ''}`;
  }, [mediaType, matchConfig]);

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;

    const tryReload = () => {
      if (cancelled) return;
      if (window.JustWatch?.reloadWidgets) {
        window.JustWatch.reloadWidgets();
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tryReload, 200);
      }
    };

    tryReload();

    return () => {
      cancelled = true;
    };
  }, [apiKey, reloadKey]);

  if (!apiKey) {
    return null;
  }

  return (
    <section
      className={`mt-12 p-6 rounded-xl border border-teal-500/30 bg-[#0F1419]/50 ${className}`}
      style={{ colorScheme: 'normal' }}
    >
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <div className="w-1 h-5 bg-teal-500 rounded-full" />
        Where to watch
      </h2>
      <div
        style={{
          backgroundColor: '#111111',
          display: 'inline-block',
          borderRadius: '4px',
          padding: '8px',
          colorScheme: 'normal',
        }}
      >
        <div
          className="inline-block"
          style={{ colorScheme: 'normal' }}
          data-jw-widget
          data-append-iframe="true"
          data-api-key={apiKey}
          data-object-type={mediaType}
          data-theme="dark"
          {...('id' in matchConfig
            ? { 'data-id': matchConfig.id, 'data-id-type': matchConfig.idType }
            : { 'data-title': matchConfig.title, ...(matchConfig.year ? { 'data-year': matchConfig.year } : {}) })}
        />
      </div>
      <div className="mt-2">
        <a
          id={brandingLinkId}
          href="https://www.justwatch.com/us/"
          target="_blank"
          rel="noopener noreferrer"
          data-original="https://www.justwatch.com"
          style={{
            fontSize: '11px',
            fontFamily: 'sans-serif',
            color: 'white',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          Powered by
          <span
            style={{
              display: 'inline-block',
              width: '66px',
              height: '10px',
              marginLeft: '3px',
              background: 'url(https://widget.justwatch.com/assets/JW_logo_color_10px.svg)',
              overflow: 'hidden',
              textIndent: '-3000px',
            }}
          >
            JustWatch
          </span>
        </a>
      </div>
    </section>
  );
}
